import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  ExternalLink, 
  BookOpen, 
  Video, 
  Headphones,
  Link as LinkIcon,
  Download
} from "lucide-react";

interface ClientResourcesViewProps {
  clientId: string;
  clinicianId: string;
}

const RESOURCE_ICONS: Record<string, any> = {
  article: FileText,
  video: Video,
  audio: Headphones,
  worksheet: BookOpen,
  link: LinkIcon
};

export function ClientResourcesView({ clientId, clinicianId }: ClientResourcesViewProps) {
  const { data: resources, isLoading } = useQuery({
    queryKey: ['client-resources-view', clientId, clinicianId],
    queryFn: async () => {
      // Fetch resources shared with this specific client
      const { data, error } = await supabase
        .from('clinician_resources')
        .select('id, title, description, resource_type, category, file_url, created_at')
        .eq('clinician_id', clinicianId)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getResourceIcon = (type: string) => {
    const Icon = RESOURCE_ICONS[type] || FileText;
    return <Icon className="h-5 w-5" />;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'anxiety': return 'bg-blue-500/10 text-blue-500';
      case 'depression': return 'bg-purple-500/10 text-purple-500';
      case 'relationships': return 'bg-pink-500/10 text-pink-500';
      case 'mindfulness': return 'bg-green-500/10 text-green-500';
      case 'self-esteem': return 'bg-yellow-500/10 text-yellow-500';
      case 'trauma': return 'bg-red-500/10 text-red-500';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  // Group resources by category
  const groupedResources = resources?.reduce((acc, resource) => {
    const category = resource.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(resource);
    return acc;
  }, {} as Record<string, typeof resources>);

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          Resources From Your Therapist
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!resources || resources.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Resources Yet</h3>
            <p className="text-sm max-w-sm mx-auto">
              Your therapist will share helpful resources, articles, and worksheets here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedResources || {}).map(([category, categoryResources]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-foreground mb-3 capitalize">
                  {category.replace('_', ' ')}
                </h3>
                <div className="grid gap-3">
                  {categoryResources?.map(resource => (
                    <div 
                      key={resource.id} 
                      className="p-4 rounded-lg bg-muted/30 border border-border/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${getCategoryColor(resource.category || 'general')}`}>
                          {getResourceIcon(resource.resource_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground truncate">{resource.title}</h4>
                          {resource.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {resource.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="secondary" className="capitalize text-xs">
                              {resource.resource_type}
                            </Badge>
                          </div>
                        </div>
                        {resource.file_url && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="shrink-0"
                            onClick={() => window.open(resource.file_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
